import to from "await-to-js";
import axios from "axios";
import { getSkyflowRecord } from "config/skyflow";
import qs from "qs";
import { actions as authActions } from "store/auth";
import { actions as flashesActions } from "store/flashes";
import { actions as uiActions } from "store/ui";
import { getRouteLocale, isFunction } from "utils/helpers";
import vars from "utils/vars";

/** Laravel/Passport responses may use `token`, `access_token`, or nested `data`. */
const extractAuthTokenFromAxiosResponse = (response) => {
	const d = response?.data;
	if (!d || typeof d !== "object") return null;
	const nested = d.data && typeof d.data === "object" ? d.data : null;
	const candidates = [d.token, d.access_token, nested?.token, nested?.access_token];
	for (const c of candidates) {
		if (c != null && String(c).trim()) return String(c).trim();
	}
	return null;
};

/** When `/auth/me` (etc.) omits token in JSON but the client sent `Authorization: Bearer …`. */
const extractAuthTokenFromAxiosRequestHeaders = (response) => {
	const h = response?.config?.headers;
	if (!h || typeof h !== "object") return null;
	const raw = h.Authorization ?? h.authorization;
	if (raw == null || typeof raw !== "string") return null;
	const trimmed = raw.trim();
	const m = /^Bearer\s+(.+)$/i.exec(trimmed);
	const value = m ? m[1].trim() : trimmed;
	return value || null;
};

/**
 * Executes an API request and handles the response.
 *
 * @param {Function|Promise} method - The API method to be executed.
 * @param {Object} options - The options object containing the data, onError, and onSuccess callbacks.
 * @param {Object} options.data - The data to be passed to the API method.
 * @param {Function} options.onError - The callback function to handle errors.
 * @param {Function} options.onSuccess - The callback function to handle success responses.
 * @throws {Error} - If the method is not passed or is not a function or a Promise.
 */
export const apiHandler = async (method, { data, onError, onSuccess }) => {
	const isMethodFunctionType = isFunction(method);
	const isMethodPromiseType = method instanceof Promise;

	// Check if the method is passed and a function or a Promise
	if (!isMethodFunctionType && !isMethodPromiseType) {
		throw new Error("method should be passed to apiHandler method as a function or a promise argument");
	}

	// Execute the API method and handle the response
	const [error, response] = await to(isMethodFunctionType ? method(data) : method);

	// Handle the error if there is one
	if (error) {
		// Check if the error is a cancelled request
		if (axios.isCancel(error)) {
			return;
		}

		// Call the onError callback if available (await async handlers)
		if (isFunction(onError)) {
			await Promise.resolve(onError(error?.response?.data || error || {}));
		}

		return;
	}

	// Call the onSuccess callback if available
	if (isFunction(onSuccess)) {
		// Only unwrap `.data` for Axios responses. Plain API bodies (e.g. getSkyflowRecord) also
		// expose a `data` key for row arrays; passing that alone breaks consumers expecting
		// `records`, `pagination`, etc.
		const isAxiosResponse = response && typeof response === "object" && response.config != null;
		const payload = isAxiosResponse ? response.data : response;
		await Promise.resolve(onSuccess(payload ?? {}));
	}
};

/**
 * Intercepts a successful API response and handles it.
 *
 * @param {Object} response - The API response object.
 * @param {Object} options - The options object containing the dispatch and demo_token properties.
 * @param {Function} options.dispatch - The Redux dispatch function.
 * @param {string} options.demo_token - The demo token.
 * @returns {Object} - The updated API response object.
 */
export const successResponseInterceptor = async function (
	response,
	{ dispatch, demo_token, navigate, language, t, skyFlowContainer }
) {
	if (response?.config?.headers?.["x-skip-response-interceptor"] === "true") {
		return response;
	}

	const responseBody = response?.data;
	const responsePayload =
		responseBody && typeof responseBody === "object" && !Array.isArray(responseBody) ? responseBody : {};
	const {
		message = null,
		user: responseUser = null,
		data: responseData = {},
		needs_password_update,
	} = responsePayload;

	// Extract the user data from the response.
	const user = responseUser || responseData?.user || {};

	// Add success messages to flash alerts.
	if (message && response?.config?.headers?.["x-prevent-flashes"] !== "true") {
		if (isFunction(dispatch)) dispatch(flashesActions.add({ type: "success", message, id: message }));
	}

	// Update user info if found in response.
	if (user && Object.keys(user).length && response?.config?.headers?.["x-prevent-update-user"] !== "true") {
		const { patient_id = null } = user;
		// UI (e.g. PatientSingleInfo) expects `mobile`; some API payloads only send `phone`.
		const userForStore = user.phone && !user.mobile ? { ...user, mobile: user.phone } : user;

		// Skyflow proxy uses `fetch` + localStorage for the Bearer token. Persist JWT before Redux
		// dispatch and before any Skyflow calls so `getSkyflowProxyPrefix()` is `auth` and PUT can hit auth proxy.
		if (patient_id && typeof window !== "undefined") {
			const fromBody = extractAuthTokenFromAxiosResponse(response);
			const fromRequest = extractAuthTokenFromAxiosRequestHeaders(response);
			const authTokenEarly = fromBody || fromRequest;
			if (authTokenEarly) window.localStorage.setItem(vars.localStorage.fieldsName.token, authTokenEarly);
		}

		// Always merge API user into Redux — including when `patient_id` is set. Previously only the
		// Skyflow path updated the store for patients, so `/auth/me` demographics were dropped if
		// Skyflow failed or was slow; Profile then showed "-" for every field.
		if (isFunction(dispatch)) {
			dispatch(
				authActions[demo_token ? "login" : "updateUser"](
					demo_token ? { token: demo_token, user: userForStore } : userForStore
				)
			);
		}

		if (patient_id) {
			await apiHandler(getSkyflowRecord, {
				data: {
					table: vars.app.skyflowTables.users,
					id: patient_id,
				},
				onError: (responseError) =>
					errorResponseInterceptor(responseError, { dispatch, navigate, language, t }),
				onSuccess: async (patientResponse = {}) => {
					// getSkyflowRecord normalizes proxy payloads to { records: [{ fields }] }; other callers use
					// records[0].fields. Reading only `.fields` left `patient` empty so Redux never got Skyflow demographics.
					successResponseInterceptor(patientResponse, { dispatch, language, t });
					const skyflowFields = patientResponse?.fields || patientResponse?.records?.[0]?.fields || {};
					const { email, email_address, logged_in_before = null, ...patientRaw } = skyflowFields;
					const patient =
						patientRaw.phone && !patientRaw.mobile
							? { ...patientRaw, mobile: patientRaw.phone }
							: patientRaw;
					if (logged_in_before) {
						dispatch(
							authActions.updateUser({
								...(userForStore || {}),
								...(patient || {}),
								...(email_address || email ? { email: email_address || email } : {}),
								logged_in_before,
							})
						);
						return;
					}

					const { createCollectContainer } = await import("utils/skyflowProxyContainers");
					const proxyCollect = skyFlowContainer?.collect ? skyFlowContainer : createCollectContainer();

					await apiHandler(proxyCollect.collect, {
						data: {
							additionalFields: {
								records: [
									{
										table: vars.app.skyflowTables.users,
										skyflow_id: patient_id,
										fields: { logged_in_before: true },
									},
								],
							},
						},
						onError: (responseError) =>
							errorResponseInterceptor(responseError, { dispatch, navigate, language, t }),
						onSuccess: () =>
							dispatch(
								authActions.updateUser({
									...(userForStore || {}),
									...(patient || {}),
									...(email_address || email ? { email: email_address || email } : {}),
									logged_in_before: true,
								})
							),
					});
				},
			});
		}
	}

	// Navigate user to update his password if he needs it.
	if (needs_password_update) {
		if (isFunction(dispatch)) dispatch(uiActions.changeUserPasswordUpdateNeeded(true));
		if (isFunction(navigate)) navigate(`${getRouteLocale(language)}/`);
	}

	// return with the modified response.
	return response;
};

/**
 * Intercepts the error response and handles it accordingly.
 *
 * @param {Object} errorResponse - The error response object.
 * @param {Object} options - The options object.
 * @param {Function} options.dispatch - The dispatch function from redux.
 * @param {Function} options.navigate - The navigate function from react-router-dom.
 * @param {string} options.language - The language of the application.
 * @param {Function} options.t - The translation function from react-i18next.
 * @param {boolean} options.isCurrentSegmentPharmacySegment - Indicates if the current segment is a pharmacy segment.
 * @param {string} options.pharmacySegmentId - The ID of the pharmacy segment.
 * @returns {object} - The updated error response object.
 */
export const errorResponseInterceptor = async function (
	errorResponse,
	{ dispatch, navigate, language, t, isCurrentSegmentPharmacySegment, pharmacySegmentId }
) {
	if (axios.isCancel(errorResponse)) return Promise.reject(errorResponse);

	// Support full Axios errors and plain bodies (e.g. apiHandler passes `error.response.data`).
	const payload = errorResponse?.response?.data ?? errorResponse;
	const status = errorResponse?.response?.status ?? errorResponse?.status;
	const { message, error, auth, data: responseData, needs_password_update, errors: rawErrors } = payload || {};

	const errorsArray = Array.isArray(rawErrors) ? rawErrors : [];
	const laravelValidationMessages =
		rawErrors && typeof rawErrors === "object" && !Array.isArray(rawErrors)
			? Object.values(rawErrors).flat().filter(Boolean)
			: [];

	const statusCodes = [status, ...(errorsArray?.map((err) => err?.error?.code || "") || [])].filter(Boolean);
	// Laravel repeats the first validation error as the top-level `message`, which used to flash the
	// same sentence twice; the flash id is the message itself, so collapsing duplicates here is enough.
	const errorMessages = [
		...new Set(
			[
				...(errorsArray?.map((err) => err?.error?.description || "") || []),
				...laravelValidationMessages,
				message || error?.message || error || "",
			].filter(Boolean)
		),
	];
	const errorMessagesLength = errorMessages?.length;
	const isChildOrderCompletedImmutable = payload?.error?.code === "CHILD_ORDER_COMPLETED_IMMUTABLE";
	const preventFlashesHeader =
		errorResponse?.config?.headers?.["x-prevent-flashes"] ||
		errorResponse?.config?.headers?.["x-prevent-error-flashes"];
	const preventPatientFlashesHeader = errorResponse?.config?.headers?.["x-prevent-patient-flashes"];
	const isFlashesVisible =
		typeof preventFlashesHeader === "undefined" || (preventFlashesHeader && preventFlashesHeader !== "true");
	const isPatientFlashesVisible =
		(typeof preventPatientFlashesHeader === "undefined" ||
			(preventPatientFlashesHeader && preventPatientFlashesHeader !== "true")) &&
		!!responseData?.error &&
		!!responseData?.patient;

	// Add error messages to flash alerts.
	if (errorMessagesLength && (isFlashesVisible || isPatientFlashesVisible || isChildOrderCompletedImmutable)) {
		if (isFunction(dispatch)) {
			errorMessages.forEach((singleMessage) =>
				dispatch(
					flashesActions.add({
						type: auth !== undefined && auth !== null && !auth ? "info" : "danger",
						message: singleMessage,
						id: singleMessage,
					})
				)
			);
		}
	}

	// Redirect to home page if user is forbidden to access the resources.
	// Completed child-order answer lock: stay on the note / requisition edit page and only show the message.
	const skipForbiddenRedirect =
		errorResponse?.config?.headers?.["x-skip-forbidden-redirect"] === "true" ||
		errorResponse?.config?.headers?.["x-skip-forbidden-redirect"] === true;
	if (
		statusCodes?.some((item) => [403]?.includes(item)) &&
		!isChildOrderCompletedImmutable &&
		!skipForbiddenRedirect
	) {
		if (isFunction(navigate)) {
			navigate(
				`${getRouteLocale(language)}${errorResponse?.config?.headers?.["x-forbidden-navigate-path"] || "/"}`
			);
		}
	}

	// Redirect to error page, either 404 not found or 500 server error.
	// `x-skip-not-found-redirect` lets a screen that owns its own inline empty/error state (e.g. a
	// settings tab whose endpoint is not deployed yet) stay put instead of losing the whole page.
	const skipNotFoundRedirect =
		errorResponse?.config?.headers?.["x-skip-not-found-redirect"] === "true" ||
		errorResponse?.config?.headers?.["x-skip-not-found-redirect"] === true;
	// The same opt-out for a 500, for a request whose failure the screen can state itself — a file
	// download, say, whose error body is a Blob nothing can read a message out of, and which has
	// no business replacing the page the clinic was reading.
	const skipServerErrorRedirect =
		errorResponse?.config?.headers?.["x-skip-server-error-redirect"] === "true" ||
		errorResponse?.config?.headers?.["x-skip-server-error-redirect"] === true;
	const isServerErrorStatus = statusCodes?.some((item) => [500]?.includes(item));
	const isNotFoundStatus = statusCodes?.some((item) => [404]?.includes(item));
	const isErrorPageSkipped = isServerErrorStatus ? skipServerErrorRedirect : skipNotFoundRedirect;
	if ((isServerErrorStatus || isNotFoundStatus) && !isErrorPageSkipped) {
		if (isFunction(navigate)) {
			navigate(
				`${getRouteLocale(language)}/error${
					isServerErrorStatus
						? `?${qs.stringify({
								code: 500,
								codeMessage: t("system_messages.server_error"),
								message: t("system_messages.server_error_message"),
						  })}`
						: ""
				}`
			);
		}
	}

	// Logout user when user not logged in on the server.
	if (![undefined, null]?.includes(auth) && !auth) {
		if (isFunction(dispatch)) dispatch(authActions.logout());
		if (isFunction(navigate))
			navigate(
				`${getRouteLocale(language)}/auth/login?${qs.stringify({
					...(isCurrentSegmentPharmacySegment ? { segment_id: pharmacySegmentId } : {}),
				})}`
			);
	}

	// Navigate user to update his password if he needs it.
	if (needs_password_update) {
		if (isFunction(dispatch)) dispatch(uiActions.changeUserPasswordUpdateNeeded(true));
		if (isFunction(navigate)) navigate(`${getRouteLocale(language)}/`);
	}

	// Reject promise with error response.
	return Promise.reject(errorResponse);
};

/**
 * Interceptor function for successful requests.
 * Adds the authorization header to the request config.
 *
 * @param {Object} config - The request configuration object.
 * @param {Object} options - The options object containing the following properties:
 * @param {boolean} options.isAuthenticated - Flag indicating if the user is authenticated.
 * @param {string} options.demo_token - The demo token.
 * @param {string} options.tenant - The tenant.
 * @returns {Object} The updated request configuration object.
 */
export const successRequestInterceptor = function (
	config,
	{ isAuthenticated, demo_token, tenant, patient_intake_token }
) {
	const localStorageToken = localStorage.getItem(vars.localStorage.fieldsName.token);
	const localStorageAcceptLanguage = localStorage.getItem(vars.localStorage.fieldsName.i18nextLng);
	const authorization = `Bearer ${localStorageToken || demo_token}`;
	const isAuthorizationTokenHeaderAllowed = Boolean((isAuthenticated && localStorageToken) || demo_token);
	const isTenantHeaderAllowed = Boolean(tenant);
	const isPatientIntakeTokenAllowed = Boolean(patient_intake_token);

	return {
		...(config || {}), // Spread the existing config object
		headers: {
			...(config.headers || {}), // Spread the existing headers object
			// Add the Accept-Language header with the language from localStorage
			"accept-language": localStorageAcceptLanguage,
			// Add the Authorization header if the user is authenticated or if a demo token is provided
			...(isAuthorizationTokenHeaderAllowed && { authorization }),
			// Add the Tenant header
			...(isTenantHeaderAllowed && { tenant }),
			// Add justmeds token if presented in the URL
			...(isPatientIntakeTokenAllowed && { "X-Patient-Intake-Token": patient_intake_token }),
		},
	};
};
